import { Card, Modal, Panel, RsPicker, Table } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Form, FormControl } from '@/components/formik-rsuite';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import React, { useState } from 'react';
import { Alert, IconButton } from 'rsuite';
import { useTable } from '@/hooks';
import useServiceBrandList from '@/hooks/preload/useServiceBrandList';
import { approveStatusData, defaultInfo } from './constants';
import _ from 'lodash';
import { fetchEditExamineReceipt, fetchExamineReceiptList } from '@/services/receipt/examine';
import { DEFAULT_TABLE_VALUE } from '@/constants/config';
import useContentHeight from '@/hooks/useContentHeight';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import useGePcsList from '@/hooks/preload/useGePcsList';
import useSalesSource from '@/hooks/preload/useSalesSource';
import ExamineCard from '../components/ExamineCard';
import useRefuseDesc from '@/hooks/preload/useRefuseDesc';
import { useReadyInfo } from '@/providers/ReadyProvider';
import * as Yup from 'yup';

const Examine = () => {
  const { profile } = useReadyInfo();
  const { data: brandList, loading: brandListLoading } = useServiceBrandList();
  const { data: gePcsList, loading: gePcsListLoading } = useGePcsList();
  const { data: salesSource, loading: salesSourceLoading } = useSalesSource();
  const { data: refuseDesc, loading: refuseDescLoading } = useRefuseDesc();

  const defaultModal = {
    show: false,
    params: null,
    refuse_desc: null,
  };
  const [showModal, setShowModal] = useState(defaultModal);

  const tableHeight = Math.max(
    useContentHeight({
      withToolBar: true,
      withPanelHeader: true,
      withPanelPaddingTop: true,
      withPanelPaddingBottom: true,
    }) - 51,
    LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
      LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
  );

  const {
    run: getExamineReceiptList,
    data: examineReceiptData,
    loading: examineReceiptLoading,
  } = useRequest<typeof DEFAULT_TABLE_VALUE>(fetchExamineReceiptList, {
    defaultParams: _.omit(
      { ...defaultInfo, shopping_village: profile.shopping_village },
      ['time_range', 'create_time']
    ),
    initialData: DEFAULT_TABLE_VALUE,
  });

  const { onChange, onSearch, onRefresh } = useTable(getExamineReceiptList, {
    start_at: defaultInfo.time_range[0],
    end_at: defaultInfo.time_range[1],
    start_create_at: defaultInfo.create_time[0],
    end_create_at: defaultInfo.create_time[1],
  });

  const { run: editExamineReceipt, loading: editExamineReceiptLoading } = useRequest(
    fetchEditExamineReceipt,
    { manual: true }
  );

  /**
   * 通过或拒绝小票
   * @param approved -1拒绝 1通过
   */
  const handleApproved = (approved = -1 | 1, params: any) => {
    approved === -1 && setShowModal(defaultModal);
    const data = { ...params, refuse_desc: showModal?.refuse_desc, approved };
    editExamineReceipt(data).then((res) => {
      if (res?.status === '200000') {
        Alert.success('提交成功');
        onRefresh();
      }
    });
  };

  const formik = useFormik({
    initialValues: { ...defaultInfo, shopping_village: profile.shopping_village },
    enableReinitialize: true,
    onSubmit(values) {
      const { brand_id, time_range = [], create_time = [], ...rest } = values;
      const params = {
        ...rest,
        brand_id: brand_id?.join(',') || '',
        start_at: time_range?.[0] || '',
        end_at: time_range?.[1] || '',
        start_create_at: create_time?.[0] || '',
        end_create_at: create_time?.[1] || '',
      };
      onSearch(params);
    },
  });

  const refuseFormik = useFormik({
    initialValues: { refuse_desc: null },
    validationSchema: Yup.object({
      refuse_desc: Yup.number().typeError('请选择拒绝原因').required('请选择拒绝原因'),
    }),
    onSubmit: (values) => {
      if (refuseFormik.isValid) {
        handleApproved(-1, showModal.params);
      }
    },
  });

  return (
    <div className="bicester-receipt-examine">
      <Panel
        title="小票审核"
        dividerHeight={15}
        className="py-[25px]"
        loading={
          brandListLoading ||
          gePcsListLoading ||
          salesSourceLoading ||
          refuseDescLoading ||
          editExamineReceiptLoading
        }
      >
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div className="flex items-center">
                <Card.Picker
                  label="购物村"
                  formKey="shopping_village"
                  pickerType="village"
                  cleanable={false}
                />
                <Card.CheckPicker
                  label="品牌"
                  formKey="brand_id"
                  pickerData={brandList}
                  searchable
                />
                <Card.Input label="手机号" formKey="phone" />
                <Card.Input
                  label="用户名"
                  formKey="name"
                  hasBtn
                  customBtn={
                    <IconButton type="submit" className="flex items-center w-[30px] ml-[10px]">
                      <Image src="/resources/images/match.svg" />
                    </IconButton>
                  }
                />
              </div>
              <div className="flex">
                <Card.Picker label="会员等级" formKey="level" pickerType="level" />
                <Card.DateRangePicker label="消费日期" formKey="time_range" />
                <Card.Picker
                  label="审核状态"
                  formKey="approved"
                  pickerData={approveStatusData}
                  cleanable={false}
                />
                <Card.DateRangePicker
                  label="上传日期"
                  placement="bottomEnd"
                  formKey="create_time"
                />
              </div>
            </Card>
          </Form>
        </FormikProvider>
        <Table
          loading={examineReceiptLoading}
          style={{
            height: tableHeight,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          onChange={onChange}
          oneRowData={{
            data: examineReceiptData?.content_list || [],
            page: examineReceiptData?.current_page || 1,
            content: (
              <div className="flex flex-wrap">
                {examineReceiptData?.content_list?.map((item, index) => (
                  <div className="w-[50%] py-[10px] px-[15px]" key={index}>
                    <ExamineCard
                      data={item}
                      brandList={brandList}
                      pcsList={gePcsList?.pcs_list || []}
                      salesSource={salesSource}
                      showModal={showModal}
                      setShowModal={setShowModal}
                      onApproved={handleApproved}
                    />
                  </div>
                ))}
              </div>
            ),
          }}
        >
          <Table.Body
            headerHeight={0}
            rowHeight={Math.max(
              Math.ceil(examineReceiptData?.content_list?.length / 2) * 646,
              tableHeight
            )}
            hover={false}
            height={Math.max(
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 51,
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
            )}
          />
          <Table.PaginationZhNoTotal />
        </Table>
      </Panel>
      <Modal
        show={showModal.show}
        isInput
        content={
          <div className="flex justify-center h-[30px] leading-[30px] mb-[20px]">
            <span className="mr-[5px]">拒绝原因</span>
            <span className="mr-[10px] text-[red]">*</span>
            <FormikProvider value={refuseFormik}>
              <Form className="flex-1">
                <FormControl
                  name="refuse_desc"
                  className="w-full"
                  accepter={RsPicker}
                  data={refuseDesc}
                  value={showModal.refuse_desc}
                  onChange={(refuse_desc) => {
                    refuseFormik.setFieldValue('refuse_desc', refuse_desc);
                    setShowModal({ ...showModal, refuse_desc });
                  }}
                />
              </Form>
            </FormikProvider>
          </div>
        }
        confirm="提交"
        cancel="取消"
        onConfirm={refuseFormik.submitForm}
        onCancel={() => {
          setShowModal(defaultModal);
          refuseFormik.setErrors({});
        }}
      />
    </div>
  );
};

export default Examine;
